/**
 * ANA-R1-09 — window-role detection for cross-window auth.
 *
 * A pop-out widget window is opened with `?popout=1` (see
 * `apps/lyse/.../multiwindow/PopoutHost.tsx`). For auth purposes the two
 * window roles behave differently:
 *
 *   - MAIN window (the opener): the single token-refresh AUTHORITY. It runs
 *     the proactive-refresh timer + the reactive 401 exchange and broadcasts
 *     each rotation to followers.
 *   - POP-OUT window (a follower): never runs its own proactive timer (two
 *     windows refreshing the SAME refresh token race — Zitadel rotates the
 *     token on each exchange, so the second window's copy is invalidated and
 *     it gets bounced to /login → a second session). A follower instead
 *     receives rotated tokens over the auth BroadcastChannel and applies them
 *     to its own (sessionStorage-isolated) store.
 *
 * `sessionStorage` is per-window-context: a `window.open` child gets a COPY of
 * the opener's session at creation but the two never sync afterwards (unlike
 * `localStorage`). That isolation is the whole reason a pop-out's auth state
 * silently diverges after the first proactive refresh in the main window. The
 * auth BroadcastChannel bridges that gap.
 */

/**
 * True when the current window is a pop-out follower (`?popout=1` on the URL).
 * Defensive against non-browser contexts (SSR / unit tests without a DOM).
 */
export function isPopoutWindow(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).get("popout") === "1";
  } catch {
    return false;
  }
}

/**
 * ANA-R1-09 (AC2/AC3) — RequireAuth's pop-out decision, extracted pure so it
 * can be unit-tested without a router/RTL harness. Returns true when a pop-out
 * window should render the inline "session ended" notice INSTEAD of redirecting
 * to /login — i.e. the main session genuinely ended (no session, wrong/unknown
 * tenant, or a tenant-slug mismatch). When false the pop-out renders normally.
 */
export function popoutShouldShowSessionEnded(args: {
  unknownTenant: boolean;
  status: string;
  hasSession: boolean;
  sessionTenantSlug: string | null;
  resolvedTenantSlug: string | null;
}): boolean {
  if (args.unknownTenant) return true;
  if (args.status === "wrong_tenant") return true;
  if (!args.hasSession) return true;
  if (
    args.resolvedTenantSlug !== null &&
    args.sessionTenantSlug !== args.resolvedTenantSlug
  ) {
    return true;
  }
  return false;
}
